import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Fade, Skeleton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { baseUrlApi } from '../constants';

const ImageGallery = ({ images, title, containerHeight = 400 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  
  // Если нет изображений, ничего не показываем
  if (!images || images.length === 0) return null;

  const nextSlide = () => {
    setLoaded(false);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevSlide = () => {
    setLoaded(false);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  // Обработчики для touch-событий (мобильные устройства)
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // Свайп влево - следующее фото
      nextSlide();
    }
    
    if (touchStart - touchEnd < -75) {
      // Свайп вправо - предыдущее фото
      prevSlide();
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Для десктопов - переключение по стрелкам клавиатуры
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleImageLoad = () => {
    setLoaded(true);
  };

  // Предпросмотр миниатюр
  const handleThumbnailClick = (index) => {
    setLoaded(false);
    setCurrentIndex(index);
  };

  return (
    <Box sx={{ width: '100%', position: 'relative', mb: 3 }}>
      {/* Основной контейнер слайдера */}
      <Box
        sx={{
          position: 'relative',
          height: containerHeight,
          overflow: 'hidden',
          borderRadius: 2,
          backgroundColor: '#f5f5f5',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Изображение с анимацией */}
        {!loaded && (
          <Skeleton 
            variant="rectangular" 
            width="100%" 
            height={containerHeight} 
            animation="wave"
          />
        )}
        
        <Fade in={loaded} timeout={300}>
          <Box
            component="img"
            src={`${baseUrlApi}${images[currentIndex]}`}
            alt={`${title} - изображение ${currentIndex + 1}`}
            onLoad={handleImageLoad}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: loaded ? 'block' : 'none',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
          />
        </Fade>

        {/* Кнопки навигации - показываем только если больше одного изображения */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={prevSlide}
              sx={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
                zIndex: 2,
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            
            <IconButton
              onClick={nextSlide}
              sx={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                },
                zIndex: 2,
              }}
            >
              <ChevronRightIcon />
            </IconButton>

            {/* Индикатор текущего изображения */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: 2,
                padding: '4px 12px',
                color: 'white',
                fontSize: '14px',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                zIndex: 2,
              }}
            >
              {currentIndex + 1} / {images.length}
            </Box>
          </>
        )}
      </Box>

      {/* Миниатюры */}
      {images.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            mt: 2,
            overflowX: 'auto',
            pb: 1,
            cursor: 'pointer',
          }}
        >
          {images.map((img, idx) => (
            <Box
              key={idx}
              onClick={() => handleThumbnailClick(idx)}
              sx={{
                width: 60,
                height: 60,
                border: idx === currentIndex ? '2px solid #1976d2' : '2px solid transparent',
                borderRadius: 1,
                overflow: 'hidden',
                opacity: idx === currentIndex ? 1 : 0.6,
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 1,
                  transform: 'scale(1.05)',
                },
              }}
            >
              <img
                src={`${baseUrlApi}${img}`}
                alt={`Миниатюра ${idx + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ImageGallery;